import { z } from "zod";

import { uf } from "./house";
import { bank } from "./banks";
import { modalityReverseCriteria, monthsToSell } from "@/constants";
import { hasFinancingSupport } from "@/helper/property";
import { preprocessNumber } from "@/helper/currency";

import type { Modality } from "@/types";

const defaultValueMessage = "Informe o valor";
const defaultPercentageMessage = "Informe a porcentagem";

const emptyNumber = z.coerce.number().optional();
const emptyNumberAsZero = z.coerce.number().optional();

// Base schema definition
export const viabilityPropertyBaseSchema = z.object({
  // General
  uf,
  valor_arrematacao: z.preprocess(
    preprocessNumber,
    z.coerce.number({ message: defaultValueMessage }).min(1, {
      message: "Informe o valor de arrematação",
    })
  ),
  valor_avaliacao: z.preprocess(
    preprocessNumber,
    z.coerce.number({ message: defaultValueMessage }).min(1, {
      message: "Informe o valor de avaliação",
    })
  ),
  porcentagem_entrada: z.preprocess(
    preprocessNumber,
    z.coerce
      .number({ message: defaultPercentageMessage })
      .min(1, { message: "Informe a porcentagem de entrada" })
      .max(100, {
        message: "A porcentagem de entrada não pode ser maior que 100",
      })
  ),
  numero_de_parcelas_entrada: z.preprocess(preprocessNumber, emptyNumber),
  juros_anual: z.preprocess(preprocessNumber, emptyNumber),
  assesoria_inicial: z.preprocess(preprocessNumber, emptyNumberAsZero),
  valor_outras_despesas_inicial: z.preprocess(
    preprocessNumber,
    emptyNumberAsZero
  ),
  // Regularization
  comissao_leiloeiro: z.preprocess(preprocessNumber, emptyNumber),
  comissao_leiloeiro_porcentagem: z.preprocess(
    preprocessNumber,
    z.coerce
      .number({ message: defaultPercentageMessage })
      .max(100, {
        message:
          "A porcentagem de comissão do leiloeiro não pode ser maior que 100",
      })
      .optional()
  ),
  taxa_itbi: z.preprocess(
    preprocessNumber,
    z.coerce
      .number({ message: defaultValueMessage })
      .min(1, { message: "Informe o ITBI" })
  ),
  taxa_itbi_porcentagem: z.preprocess(
    preprocessNumber,
    z.coerce
      .number({ message: defaultPercentageMessage })
      .max(100, {
        message: "A porcentagem de ITBI não pode ser maior que 100",
      })
      .optional()
  ),
  taxa_escritura: z.preprocess(preprocessNumber, emptyNumber),
  taxa_escritura_porcentagem: z.preprocess(preprocessNumber, emptyNumber),
  taxa_registro: z.preprocess(
    preprocessNumber,
    z.coerce
      .number({ message: defaultValueMessage })
      .min(1, { message: "Informe o valor do registro" })
  ),
  taxa_registro_porcentagem: z.preprocess(
    preprocessNumber,
    z.coerce
      .number({ message: defaultPercentageMessage })
      .max(100, {
        message: "A porcentagem de registro não pode ser maior que 100",
      })
      .optional()
  ),
  taxa_frj: z.preprocess(preprocessNumber, emptyNumber),
  valor_desocupacao: z.preprocess(preprocessNumber, emptyNumberAsZero),
  dividas_propter_rem: z.preprocess(preprocessNumber, emptyNumberAsZero),
  valor_outras_despesas_regularizacao: z.preprocess(
    preprocessNumber,
    emptyNumberAsZero
  ),
  // Sale
  valor_reforma: z.preprocess(preprocessNumber, emptyNumberAsZero),
  comissao_corretor: z.preprocess(preprocessNumber, emptyNumber),
  comissao_corretor_porcentagem: z.preprocess(
    preprocessNumber,
    z.coerce
      .number({ message: defaultPercentageMessage })
      .max(100, {
        message:
          "A porcentagem de comissão do corretor não pode ser maior que 100",
      })
      .optional()
  ),
  assesoria_venda: z.preprocess(preprocessNumber, emptyNumberAsZero),
  assesoria_venda_porcentagem: z.preprocess(
    preprocessNumber,
    z.coerce
      .number({ message: defaultPercentageMessage })
      .max(100, {
        message:
          "A porcentagem de comissão da assessoria de venda não pode ser maior que 100",
      })
      .optional()
  ),
  trafego_pago: z.preprocess(preprocessNumber, emptyNumberAsZero),
  taxa_irpf: z.preprocess(preprocessNumber, emptyNumber),
  valor_outras_despesas_venda: z.preprocess(
    preprocessNumber,
    emptyNumberAsZero
  ),
  // Monthly
  frequencia_iptu: z.enum(["mensal", "anual"]),
  valor_iptu: z.preprocess(
    preprocessNumber,
    z.coerce
      .number({ message: defaultValueMessage })
      .min(1, { message: "Informe o valor do IPTU" })
  ),
  valor_condominio: z.preprocess(preprocessNumber, emptyNumber),
  // Result
  prazo_de_venda_meses: z.preprocess(
    preprocessNumber,
    z.coerce
      .number({ message: defaultValueMessage })
      .optional()
      .transform((val) => val || monthsToSell)
  ),
  // Custom
  banco: bank.optional(),
  modalidade_de_venda: z
    .enum(Object.keys(modalityReverseCriteria) as [Modality, ...Modality[]])
    .optional(),
  tipo: z.string().optional(),
});

export const viabilityPropertySchema = viabilityPropertyBaseSchema.superRefine(
  (data, ctx) => {
    const isFinancing =
      hasFinancingSupport(
        data.modalidade_de_venda,
        data.porcentagem_entrada
      ) === "financing";

    if (
      isFinancing &&
      (data.juros_anual === undefined || data.juros_anual === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Taxa de juros anual é obrigatória quando há financiamento",
        path: ["juros_anual"],
      });
    }

    if (
      isFinancing &&
      (data.comissao_leiloeiro === undefined || data.comissao_leiloeiro === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Comissão do leiloeiro é obrigatória quando há financiamento ou a modalidade de venda exige comissão de leiloeiro",
        path: ["comissao_leiloeiro"],
      });
    }
  }
);

export const viabilityCreationSchema = z.object({
  id: z.string().optional(),
  leilao_id: z.string().optional(),
  titulo: z.string({ message: "Informe o título do cálculo" }).min(1, {
    message: "Informe o título do cálculo",
  }),
});

export const viabilityDeletionSchema = z.object({
  leilao_id: z.string(),
});

export const viabilitySchema = viabilityPropertyBaseSchema.merge(
  viabilityCreationSchema
);

export const viabilitesSchema = z.array(
  viabilitySchema.pick({ id: true, titulo: true }).extend({
    id: z.string(),
    lucro_estimado: z.number(),
  })
);
